/*
 * Optim product card — shared per-card image slider.
 * ONE deferred, browser-cached copy for every place the optim card is used
 * (optim-featured-collection, destination-featured-collection,
 *  main-collection-product-grid). Replaces the identical inline scripts those
 *  sections each used to ship.
 *
 * Behaviour is unchanged from the old inline versions:
 *  - Native scroll-snap image slider (no library).
 *  - Secondary slide images hold a transparent placeholder and are fetched on
 *    demand via an IntersectionObserver rooted on the slider (eager fallback
 *    when IO is unavailable).
 *  - Dots + arrows sync on scroll (rAF-debounced); arrows disable at the ends.
 *  - Idempotent: `data-rec-slider-init` guards each card, so initAll() is safe
 *    to call again after Swiper tail-hydration or an AJAX filter/sort re-render.
 *
 * API:  window.OptimCardSlider.init(cardEl)
 *       window.OptimCardSlider.initAll(scopeEl | document)
 * Auto-initialises the whole document once on DOMContentLoaded.
 */
(function () {
  // Load-once guard: several sections on one page may reference this file
  // (e.g. multiple destination sliders + the collection grid). The browser
  // caches the file; this makes every execution after the first a no-op.
  if (window.OptimCardSlider) return;

  /*
   * Shared image loader, hoisted to module scope so the card-level observer
   * below can reuse it.
   */
  function loadImg(img) {
    if (!img || !img.dataset.src) return;
    img.classList.add('is-loading'); // shows the shimmer until it decodes
    img.addEventListener(
      'load',
      function () {
        img.classList.remove('is-loading');
        img.classList.add('is-loaded');
      },
      { once: true }
    );
    img.addEventListener(
      'error',
      function () {
        img.classList.remove('is-loading');
      },
      { once: true }
    );
    if (img.dataset.srcset) img.srcset = img.dataset.srcset;
    img.src = img.dataset.src;
    img.removeAttribute('data-src');
    img.removeAttribute('data-srcset');
  }

  /*
   * FIRST-SWIPE WARM-UP
   * -------------------
   * Arrow and dot clicks already call preload() before scrolling, so desktop
   * never waits. A touch swipe uses native scroll-snap and never goes through
   * that path, so on mobile the request for slide 2 only started once slide 2
   * was already sliding into view — which is the visible pause on every swipe.
   *
   * This warms ONLY slide 2, and only once the card itself is near the page
   * viewport. Deliberately not "load every image of every visible card": that
   * would be 5 requests per card, most of them for images nobody opens. One
   * extra image per on-screen card buys an instant first swipe; sync() below
   * then keeps the chain one ahead for every swipe after that.
   *
   * One observer for all cards rather than one per card, and it unobserves on
   * first hit. rootMargin starts the fetch slightly before the card is on
   * screen. Runs after the card is visible, so it never competes with LCP.
   */
  var cardWarmObserver = null;
  function warmUpCard(card) {
    var slides = card.querySelectorAll('.rec-card__slide');
    if (slides.length < 2) return;
    loadImg(slides[1].querySelector('img[data-src]'));
  }
  function observeCardForWarmUp(card) {
    if (!('IntersectionObserver' in window)) {
      warmUpCard(card);
      return;
    }
    if (!cardWarmObserver) {
      cardWarmObserver = new IntersectionObserver(
        function (entries, obs) {
          entries.forEach(function (e) {
            if (!e.isIntersecting) return;
            obs.unobserve(e.target);
            warmUpCard(e.target);
          });
        },
        { rootMargin: '200px 0px' }
      );
    }
    cardWarmObserver.observe(card);
  }

  /*
   * POINTER-AWARE TEXT SELECTION
   * ----------------------------
   * The card body carries Swiper's `swiper-no-swiping` class ONLY on devices
   * with a mouse. There it makes titles/prices selectable: Swiper ignores a
   * drag that starts on the body, while dragging the image still moves the
   * carousel. On touch devices the class must NOT be present — the inner image
   * slider already claims the image area, so a no-swiping body would leave
   * nowhere on the card to swipe the parent carousel from (the mobile bug
   * this replaces). Evaluated per device rather than baked into the markup,
   * and re-evaluated if a hybrid device docks/undocks a mouse.
   */
  var finePointer = window.matchMedia ? window.matchMedia('(hover: hover) and (pointer: fine)') : null;
  function applyBodySwipeMode(card) {
    var body = card.querySelector('.rec-card__body');
    if (!body) return;
    body.classList.toggle('swiper-no-swiping', !!(finePointer && finePointer.matches));
  }
  if (finePointer && typeof finePointer.addEventListener === 'function') {
    finePointer.addEventListener('change', function () {
      document.querySelectorAll('.rec-card').forEach(applyBodySwipeMode);
    });
  }

  function initInnerSlider(card) {
    if (card.dataset.recSliderInit) return;
    card.dataset.recSliderInit = '1';

    applyBodySwipeMode(card);

    var slider = card.querySelector('.rec-card__slider');
    if (!slider) return;
    var slides = slider.querySelectorAll('.rec-card__slide');
    var dots = card.querySelectorAll('.rec-card__dot');
    var prev = card.querySelector('.rec-card__arrow--prev');
    var next = card.querySelector('.rec-card__arrow--next');
    if (!slides.length) return;

    /*
     * Scroll geometry is physical and CSS logical properties cannot fix it.
     * In an RTL scroll container the spec-compliant `scrollLeft` starts at 0
     * at the right edge and grows NEGATIVE toward the end. Left unhandled,
     * activeIndex() returns negative numbers (so no dot ever lights up),
     * `next` is born disabled, and step() scrolls away from the images.
     *
     * Direction is read from computed style, not the locale, so this follows
     * whatever <html dir> inherits down and needs no locale list.
     */
    function isRTL() {
      return getComputedStyle(slider).direction === 'rtl';
    }

    /* Distance travelled from the start edge, direction-agnostic. */
    function scrolled() {
      return Math.abs(slider.scrollLeft);
    }

    function activeIndex() {
      return Math.round(scrolled() / slides[0].offsetWidth);
    }
    function sync() {
      var idx = activeIndex();
      dots.forEach(function (d, i) {
        d.classList.toggle('is-active', i === idx);
      });
      var maxScroll = slider.scrollWidth - slider.clientWidth;
      if (prev) prev.disabled = scrolled() <= 5;
      if (next) next.disabled = scrolled() >= maxScroll - 5;

      // Stay one slide ahead in whichever direction the visitor is heading.
      // sync() runs rAF-debounced on every scroll, including touch swipes, so
      // this is what keeps swipes 2..N instant once the card is in use. preload()
      // is a no-op for anything already fetched, so this costs nothing to repeat.
      preload(idx + 1);
      preload(idx - 1);
    }

    /* On-demand image loading uses the shared module-scope loadImg(). */
    function preload(index) {
      if (index < 0 || index >= slides.length) return;
      loadImg(slides[index].querySelector('img[data-src]'));
    }
    function step(dir) {
      preload(activeIndex() + dir); // fetch the target before we scroll to it
      // scrollBy takes a PHYSICAL delta, so forward is negative in RTL.
      var delta = dir * slides[0].offsetWidth;
      slider.scrollBy({ left: isRTL() ? -delta : delta, behavior: 'smooth' });
    }

    var lazyImgs = slider.querySelectorAll('img[data-src]');
    if (lazyImgs.length && 'IntersectionObserver' in window) {
      // Load a secondary image only as it scrolls into THIS card's viewport.
      var imgIO = new IntersectionObserver(
        function (entries, obs) {
          entries.forEach(function (e) {
            if (!e.isIntersecting) return;
            loadImg(e.target);
            obs.unobserve(e.target);
          });
        },
        { root: slider, threshold: 0.01 }
      );
      lazyImgs.forEach(function (img) {
        imgIO.observe(img);
      });
    } else {
      // No IntersectionObserver → just load them (graceful fallback).
      lazyImgs.forEach(loadImg);
    }

    if (prev) {
      prev.addEventListener('click', function (e) {
        e.stopPropagation();
        step(-1);
      });
    }
    if (next) {
      next.addEventListener('click', function (e) {
        e.stopPropagation();
        step(1);
      });
    }
    dots.forEach(function (d) {
      d.addEventListener('click', function (e) {
        e.stopPropagation();
        var i = parseInt(d.getAttribute('data-index'), 10) || 0;
        preload(i);
        // Offset RELATIVE to the first slide, which is direction-agnostic:
        // in LTR slide 0 is leftmost so the delta is positive and matches
        // scrollLeft; in RTL slide 0 is rightmost so the delta is negative,
        // which is exactly the range RTL scrollLeft uses. Passing the raw
        // offsetLeft instead would clamp to 0 in RTL and always jump back to
        // the first image.
        slider.scrollTo({ left: slides[i].offsetLeft - slides[0].offsetLeft, behavior: 'smooth' });
      });
    });
    slider.addEventListener('scroll', function () {
      window.requestAnimationFrame(sync);
    });

    // Finger-down is the earliest possible signal of intent — it precedes the
    // drag, so the neighbouring image is usually decoded before the swipe even
    // finishes. Passive: this never calls preventDefault, so it must not block
    // scrolling.
    slider.addEventListener(
      'pointerdown',
      function () {
        var idx = activeIndex();
        preload(idx + 1);
        preload(idx - 1);
      },
      { passive: true }
    );

    observeCardForWarmUp(card);
    sync();
  }

  function initAll(scope) {
    (scope || document).querySelectorAll('.rec-card').forEach(initInnerSlider);
  }

  window.OptimCardSlider = { init: initInnerSlider, initAll: initAll };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initAll(document);
    });
  } else {
    initAll(document);
  }
})();
